// Formulário de criação. A configuração nasce imutável — por isso o aviso no
// topo e o resumo ao vivo ao lado: é a última chance de conferir a regra.
import { zodResolver } from "@hookform/resolvers/zod"
import {
  BanknoteIcon,
  Loader2Icon,
  PercentIcon,
} from "lucide-react"
import { useEffect, useMemo } from "react"
import type { FieldErrors } from "react-hook-form"
import { get, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import type { WhiteLabel } from "@/hooks/use-white-labels"
import type { PaymentConfigurationCreatePayload } from "@/types/payment-configurations"

import {
  buildCreatePaymentConfigurationSchema,
  CREATE_FIELD_ORDER,
  createPaymentConfigurationDefaults,
  type CreatePaymentConfigurationValues,
  DESCRIPTION_MAX_LENGTH,
  PIX_AUTO_PRE_CHARGE_MAX,
  PIX_AUTO_PRE_CHARGE_MIN,
  toCreatePaymentConfigurationPayload,
} from "../schemas"
import { ChargeRuleSection } from "./charge-rule-section"
import { ImmutabilityNotice } from "./immutability-notice"
import { PaymentConfigurationSummarySidebar } from "./payment-configuration-summary-sidebar"

const prefix = "pages.paymentConfigurations"

const SECTION_LABEL_CLASS =
  "mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"

interface CreatePaymentConfigurationFormProps {
  whiteLabels: WhiteLabel[]
  isLoadingWhiteLabels: boolean
  /** White label sugerido (ex.: o do filtro da listagem). */
  initialWhiteLabelId?: string
  isSubmitting: boolean
  onSubmit: (
    payload: PaymentConfigurationCreatePayload
  ) => void
  onCancel: () => void
}

type DaysFieldName =
  | "daysDue"
  | "daysAfterDue"
  | "preChargeDays"
  | "pixAutoPreChargeDays"

// Campos de dias, na ordem visual (a mesma do CREATE_FIELD_ORDER).
const DAYS_FIELDS: ReadonlyArray<{
  name: DaysFieldName
  required: boolean
}> = [
  { name: "daysDue", required: false },
  { name: "daysAfterDue", required: true },
  { name: "preChargeDays", required: false },
  { name: "pixAutoPreChargeDays", required: false },
]

// Sugestão válida: a da URL se for um white label do usuário; senão, o único.
const resolveInitialWhiteLabel = (
  whiteLabels: WhiteLabel[],
  suggested: string | undefined
) => {
  if (
    suggested &&
    whiteLabels.some((item) => item.uuid === suggested)
  ) {
    return suggested
  }
  return whiteLabels.length === 1 ? whiteLabels[0].uuid : ""
}

export const CreatePaymentConfigurationForm = ({
  whiteLabels,
  isLoadingWhiteLabels,
  initialWhiteLabelId,
  isSubmitting,
  onSubmit,
  onCancel,
}: CreatePaymentConfigurationFormProps) => {
  const { t } = useTranslation()

  const schema = useMemo(
    () =>
      buildCreatePaymentConfigurationSchema({
        whiteLabelRequired: t(
          `${prefix}.validation.whiteLabelRequired`
        ),
        descriptionTooLong: t(
          `${prefix}.validation.descriptionTooLong`,
          { max: DESCRIPTION_MAX_LENGTH }
        ),
        daysRequired: t(
          `${prefix}.validation.daysRequired`
        ),
        daysInvalid: t(`${prefix}.validation.daysInvalid`),
        pixAutoRange: t(
          `${prefix}.validation.pixAutoRange`,
          {
            min: PIX_AUTO_PRE_CHARGE_MIN,
            max: PIX_AUTO_PRE_CHARGE_MAX,
          }
        ),
        amountRequired: t(
          `${prefix}.validation.amountRequired`
        ),
        amountInvalid: t(
          `${prefix}.validation.amountInvalid`
        ),
        amountPositive: t(
          `${prefix}.validation.amountPositive`
        ),
        percentageInvalid: t(
          `${prefix}.validation.percentageInvalid`
        ),
        percentageMax: t(
          `${prefix}.validation.percentageMax`
        ),
      }),
    [t]
  )

  const form = useForm<CreatePaymentConfigurationValues>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: createPaymentConfigurationDefaults(),
  })

  // Os white labels chegam depois do mount: a sugestão entra quando a lista
  // chega, sem sujar o form e sem atropelar uma escolha do usuário.
  const suggestedWhiteLabel = resolveInitialWhiteLabel(
    whiteLabels,
    initialWhiteLabelId
  )
  useEffect(() => {
    if (
      suggestedWhiteLabel &&
      !form.getValues("whiteLabelId")
    ) {
      form.resetField("whiteLabelId", {
        defaultValue: suggestedWhiteLabel,
      })
    }
  }, [form, suggestedWhiteLabel])

  const whiteLabelNameOf = (id: string) =>
    whiteLabels.find((item) => item.uuid === id)?.name ?? id

  // Foco no PRIMEIRO inválido na ordem da tela (rule 10 §7).
  const handleInvalid = (
    errors: FieldErrors<CreatePaymentConfigurationValues>
  ) => {
    const first = CREATE_FIELD_ORDER.find((path) =>
      Boolean(get(errors, path))
    )
    if (first) form.setFocus(first)
  }

  const handleSubmit = form.handleSubmit(
    (values) =>
      onSubmit(toCreatePaymentConfigurationPayload(values)),
    handleInvalid
  )

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_300px]">
          <div className="space-y-6">
            <ImmutabilityNotice />

            <Card className="border-0 shadow-sm">
              <CardContent className="space-y-8 pt-6">
                <section aria-labelledby="pcf-identity">
                  <h2
                    id="pcf-identity"
                    className={SECTION_LABEL_CLASS}
                  >
                    {t(`${prefix}.form.sections.identity`)}
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="whiteLabelId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t(
                              `${prefix}.fields.whiteLabel`
                            )}
                          </FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isLoadingWhiteLabels}
                          >
                            <FormControl>
                              <SelectTrigger
                                ref={field.ref}
                                onBlur={field.onBlur}
                              >
                                <SelectValue
                                  placeholder={t(
                                    `${prefix}.form.whiteLabelPlaceholder`
                                  )}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {whiteLabels.map(
                                (whiteLabel) => (
                                  <SelectItem
                                    key={whiteLabel.uuid}
                                    value={whiteLabel.uuid}
                                  >
                                    {whiteLabel.name}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t(
                              `${prefix}.fields.description`
                            )}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              autoComplete="off"
                              maxLength={
                                DESCRIPTION_MAX_LENGTH
                              }
                              placeholder={t(
                                `${prefix}.form.descriptionPlaceholder`
                              )}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </section>

                <Separator />

                <section aria-labelledby="pcf-due">
                  <h2
                    id="pcf-due"
                    className={SECTION_LABEL_CLASS}
                  >
                    {t(`${prefix}.form.sections.due`)}
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {DAYS_FIELDS.map(
                      ({ name, required }) => (
                        <FormField
                          key={name}
                          control={form.control}
                          name={name}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {t(
                                  `${prefix}.fields.${name}`
                                )}
                                {!required && (
                                  <span className="ml-1 font-normal text-muted-foreground">
                                    {t(
                                      `${prefix}.form.optional`
                                    )}
                                  </span>
                                )}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  inputMode="numeric"
                                  autoComplete="off"
                                  className="tabular-nums"
                                  aria-required={required}
                                />
                              </FormControl>
                              <FormDescription>
                                {t(
                                  `${prefix}.hints.${name}`
                                )}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )
                    )}
                  </div>
                  <FormField
                    control={form.control}
                    name="generateAllInvoices"
                    render={({ field }) => (
                      <FormItem className="mt-4 flex items-start justify-between gap-4 rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel>
                            {t(
                              `${prefix}.fields.generateAllInvoices`
                            )}
                          </FormLabel>
                          <FormDescription>
                            {t(
                              `${prefix}.hints.generateAllInvoices`
                            )}
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </section>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="space-y-6 pt-6">
                <ChargeRuleSection
                  control={form.control}
                  name="fine"
                  icon={BanknoteIcon}
                  title={t(`${prefix}.fields.fine`)}
                  description={t(`${prefix}.hints.fine`)}
                />
                <Separator />
                <ChargeRuleSection
                  control={form.control}
                  name="interest"
                  icon={PercentIcon}
                  title={t(`${prefix}.fields.interest`)}
                  description={t(
                    `${prefix}.hints.interest`
                  )}
                />
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-5 py-4 shadow-sm">
              <p className="text-xs text-muted-foreground">
                {form.formState.isDirty
                  ? t(`${prefix}.form.dirty`)
                  : t(`${prefix}.form.pristine`)}
              </p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  className="min-w-[140px] gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2Icon
                        className="h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                      {t(`${prefix}.form.submitting`)}
                    </>
                  ) : (
                    t(`${prefix}.form.submit`)
                  )}
                </Button>
              </div>
            </div>
          </div>

          <PaymentConfigurationSummarySidebar
            control={form.control}
            whiteLabelNameOf={whiteLabelNameOf}
          />
        </div>
      </form>
    </Form>
  )
}
